import React, { useState } from 'react';
import { Close as CloseIcon, GetApp as GetAppIcon } from '@mui/icons-material';
import { Box, Modal, IconButton } from '@mui/material';
import { FilePath } from '@askporter/client-grieg-lyric';
import { AllowedFileTypes, getFileDetails } from '@askporter/utils';
import { Button } from '../../../Buttons/Button';

/** The header bar is fixed at the top of the modal, so the previewed document starts below it. */
const HEADER_HEIGHT = '80px';

interface FilePreviewProps {
  isSmallDevice?: boolean;
  handleClose: () => void;
  file: FilePath;
  title?: string;
  t: (key: string, options?: Record<string, string | number>) => string;
}

export const FilePreview = ({ isSmallDevice, handleClose, file, title, t }: FilePreviewProps): JSX.Element => {
  const [full, setFull] = useState(false);

  const handleImageClick = () => {
    if (!isSmallDevice) setFull(!full);
  };

  // A PDF can never render in an <img>, so it goes to the browser's own viewer instead. Every other type keeps
  // the image element it has always used — including the zoom toggle, which a PDF viewer provides itself.
  const isPdf = getFileDetails(file?.filePath).type === AllowedFileTypes.PDF;

  return (
    <Modal open onClose={handleClose} data-testid="file-preview">
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          bottom: 0,
          width: '100%',
          background: 'rgba(0, 0, 0, 0.3)',
          zIndex: 999,
          overflowY: 'scroll',
        }}
      >
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            backgroundColor: '#212121',
            height: HEADER_HEIGHT,
            padding: 4,
          }}
        >
          <Box>
            <IconButton
              onClick={handleClose}
              aria-label={t('ns.common:modal:close_button:aria_label')}
              color="secondary"
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box position="absolute" top="16px" right="16px">
            {isSmallDevice ? (
              <IconButton
                aria-label={`${t('ns.task:media:download_button')}`}
                color="secondary"
                component="a"
                href={file?.filePath}
                target="_blank"
                rel="noopener noreferrer"
              >
                <GetAppIcon />
              </IconButton>
            ) : (
              <Button
                data-testid={`download-button`}
                variant="contained"
                color="secondary"
                endIcon={<GetAppIcon />}
                component="a"
                href={file?.filePath}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('ns.task:media:download_button')}
              </Button>
            )}
          </Box>
        </Box>

        {isPdf ? (
          <Box
            component="iframe"
            src={file.filePath}
            // Falls back to the path so the frame is always named for assistive technology — most callers pass
            // no title
            title={title ?? file.filePath}
            sx={{
              display: 'block',
              width: '100%',
              // Sits below the fixed header rather than under it, so the viewer's own toolbar stays reachable
              height: `calc(100% - ${HEADER_HEIGHT})`,
              mt: HEADER_HEIGHT,
              border: 'none',
            }}
          />
        ) : (
          <Box onClick={handleImageClick} onKeyPress={handleImageClick} role="button" sx={{ height: '100%' }}>
            <img
              src={file.filePath}
              alt={title}
              style={
                full
                  ? { cursor: 'zoom-out' }
                  : { cursor: 'zoom-in', height: '100%', width: '100%', objectFit: 'contain' }
              }
            />
          </Box>
        )}
      </Box>
    </Modal>
  );
};
